<script>
  import { Username, FromPage } from "./store.js";
  import { onMount, createEventDispatcher } from "svelte";
  const dispatch = createEventDispatcher();
  import * as Validator from "../Utils/ValidateFormFIelds.js";
  import Field from "../FormField.svelte";
  import TextBox from "../HTMLElements/Textbox.svelte";
  import Email from "../HTMLElements/Email.svelte";
  import Phone from "../HTMLElements/PhoneNumber.svelte";
  import wrappedFetch from '../../wrappedfetch.js';
  import AccountMatchModal from "./AccountMatchModal.svelte";

  let showAccountMatchModal = false;
  let recoveredUsername = "";

  let isFormValid = true;
  let error = "";
  let postcodeError = '';
  let postcodeHasError = false;
  let isValidating = false;

  // Staff USO accounts have no home postcode on record, so this page's
  // email/postcode/mobile lookup can never identify them. The LGfL
  // self-service page identifies staff by DATE OF BIRTH instead — send
  // them there. returnto gives its final step a link back to our sign-in.
  // NB the link MUST be rel="noreferrer": that page sniffs the Referrer
  // header and switches into eAdmissions mode (postcode shown, DOB
  // hidden) whenever it sees eadmissions.org.uk — exactly what staff
  // must not get.
  const lgflRecoveryUrl =
    "https://support.lgfl.org.uk/public/changepass.aspx?returnto=" +
    encodeURIComponent(window.location.origin + "/signin");
  // null | 'matched' | 'prompt' — 'matched': the lookup positively found a
  // staff account (show ONE tailored panel, suppressing the server's
  // registration-worded message so the user doesn't get two overlapping
  // explanations); 'prompt': nothing found, so ask the school question.
  let staffPanelMode = null;
  const email = { value: "", isValid: false, isDirty: false};
  const mobile = { value: "", isValid: false, isDirty: false };
  const postcode = { value: ""}; // not using address picker. validate locally.
  let ref = null;

  onMount(() => {
    ref.focus();
  });

  const validate = async e => {
    e.preventDefault();

    isValidating = true;
    staffPanelMode = null;

    if ( ( (email.value == "" ? 0 : 1) + (mobile.value == "" ? 0 : 1 ) + (postcode.value == ""? 0 : 1) ) < 2) {
      isFormValid = false;
      error = "Please complete at least 2 fields above.";
      isValidating = false;
      return;
    }
    var obj = {};
    obj.email = email;
    obj.mobile = mobile;

    if (!Validator.validate(obj)) {
      obj = obj;
      error =
        "Please ensure that you have completed the required fields.";
      isFormValid = false;
      isValidating = false;
      return;
    }

    var userIfExists = await wrappedFetch(
      "user/FindForgottenUsername?email=" +
        email.value +
        "&mobile=" +
        mobile.value +
        "&postcode=" +
        postcode.value
    )
      .then(res => res.json())
      .then(result => {
        return result;
      });

    if (userIfExists.success) {
      $Username.value = userIfExists.message;
      FromPage.set("username");
      dispatch("pageChange", "/account/usernamefound");
    } else {
        // Archived/recovered account — confirm via masked preview, then verify.
        if (userIfExists.recovered) {
          $Username.value = userIfExists.username;
          FromPage.set("username");
          recoveredUsername = userIfExists.username;
          showAccountMatchModal = true;
          isValidating = false;
          return;
        }

        // Show the staff route at the moment it matters — after a failed
        // lookup. Users don't read preamble text; they fill in the boxes,
        // press Continue, and read whatever appears.
        if (userIfExists.isStaff) {
          // Positively matched a staff account: ONE tailored panel; the
          // server's message is registration-worded ("links below") and
          // would contradict the panel, so it's suppressed here.
          error = "";
          staffPanelMode = "matched";
        } else {
          error = userIfExists.message;
          staffPanelMode = "prompt";
        }
        $Username.isValid = false;
        isFormValid = false;
    }

    isValidating = false;
  };

  const validatePostcode = e => {
    var postcodeRegex = /^(GIR 0AA|[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})$/i;
		
    postcode.value = postcode.value.replace(/(\S*)\s*(\d)/, "$1 $2").toUpperCase();
    if(postcode.value != ''){
      if(postcode.value.replace(/\s/g, "").length < 5){
        postcodeError = "Invalid postcode."
        postcodeHasError = true;
        return;
      }
      if (!postcodeRegex.test(postcode.value)) {
        postcodeHasError = true;
        postcodeError = "Postcode is not valid or incomplete. Please enter the full postcode and try again";
        return
      }

    wrappedFetch("/addresses/validateaddressbypostcode?postcode=" + postcode.value)
      .then(res => res.json())
      .then(res => {
        if (res.success)
        {
          if (res.data > 0)
          {
            postcodeError = '';
            postcodeHasError = false;
          }
          else
          {
            postcodeError = "Postcode was not found.";
            postcodeHasError = true;
          }
        }
        else
        {
        postcodeError = "Postcode was not found.";
        postcodeHasError = true;
        }
      });
    } else{
      postcodeHasError = false;
    }
  };
</script>

<form action="" class="box" autocomplete="off">
  <div class=" field">
    <a href="/account/forgotpassword" tabindex="0">
      <span class="fas fa-arrow-left" />
      Back
    </a>
  </div>

  <hr />

  <h1 class="title is-4">Find your username</h1>
  <p class="subtitle is-6">
    At least two of the following three pieces of information should be provided
    so that your account can be identified.
  </p>

  <Field label="Email address" hasIconLeft="true"  isRequired={ (email.value == "" ? 0 : 1) + (mobile.value == "" ? 0 : 1 ) + (postcode.value == ""? 0 : 1)  < 2 }>
    <span slot="inputField">
      <Email
        placeholder="email@domain.com"
        isRequired={mobile.value == '' && postcode.value == '' ? true : false}
        bind:email={email.value}
        bind:isValid={email.isValid}
        bind:isDirty={email.isDirty}
        label="email address"
        bind:ref
        faIcon="fas fa-envelope" />
    </span>
  </Field>

  <Field label="Home Postcode" hasIconLeft="true" isRequired={ (email.value == "" ? 0 : 1) + (mobile.value == "" ? 0 : 1 ) + (postcode.value == ""? 0 : 1)  < 2 } >
    <span slot="inputField">
      <TextBox
        placeholder="AB1 2BA"
        isRequired={email.value == '' && mobile.value == '' ? true : false}
        bind:text={postcode.value}
        on:blur={validatePostcode}
        label="home postcode"
        faIcon="fas fa-map-marker-alt" />
        {#if postcodeHasError}
          <p class="help has-text-danger">{postcodeError}</p>
        {/if}
    </span>
  </Field>
 
  <Field label="Mobile Number" hasIconLeft="true" isRequired={ (email.value == "" ? 0 : 1) + (mobile.value == "" ? 0 : 1 ) + (postcode.value == ""? 0 : 1)  < 2 } >
    <span slot="inputField">
      <Phone
        placeholder="07xxx xxxxxx"
        isRequired={email.value == '' && postcode.value == '' ? true : false}
        bind:number={mobile.value}
        bind:isValid={mobile.isValid}
        bind:isDirty={mobile.isDirty}
        isMobile="true"
        label="mobile number"
        faIcon="fas fa-mobile-alt" />
    </span>
  </Field>
  <p class="help">
    School staff?
    <a href={lgflRecoveryUrl} target="_blank" rel="noopener noreferrer"
       style="text-decoration:underline;">Recover your staff account username here</a>.
  </p>
  <hr />

  {#if !isFormValid && error}
    <div class="notification is-invalid">
      <p class="help" style="margin-bottom:0px">{@html error}</p>
    </div>
  {/if}

  {#if staffPanelMode}
    <!-- Shown after a failed lookup — the one moment the user is reading. -->
    <div class="notification" style="background:#f5f0f6;">
      {#if staffPanelMode === 'matched'}
        <p class="help">
          <strong>We've found an existing staff account.</strong><br />
          An account matching the details you entered implies that you work,
          or have worked, in a school — for example as a School Governor or a
          Teaching Assistant. Your username is usually the initial of your
          first name, then your surname, then a number, then a dot, then a
          three-digit number — e.g. <strong>jsmith4.123</strong>.
        </p>
        <p class="help">
          If you remember your sign-in details, please
          <a href="/signin" style="text-decoration:underline;">sign in</a>.
          Otherwise, recover your username on the USO self-service page:
        </p>
      {:else}
        <p class="help">
          <strong>Do you work, or have you worked, in a school?</strong><br />
          If so, your account is a staff USO account. It has no home postcode
          registered, so the search on this page cannot find it — staff
          accounts are identified by <strong>date of birth</strong> instead.
        </p>
      {/if}
      <!-- Real <button> so it styles like every other button (an anchor
           picks up the site's link typography and overflows the frame).
           window.open with "noreferrer" omits the Referer header — REQUIRED,
           the changepass page switches to postcode mode on our referrer. -->
      <button class="button is-purple is-fullwidth"
              on:click|preventDefault={() => window.open(lgflRecoveryUrl, "_blank", "noopener,noreferrer")}>
        Recover my staff account username
      </button>
      <p class="help" style="margin-top:0.5rem; margin-bottom:0;">
        Opens in a new tab. Provide two of: email address, mobile number,
        date of birth. Once you have your username, return here and sign in
        as normal.
      </p>
    </div>
  {/if}
  <div style="text-align: right;">
    <button
      class="button is-purple {isValidating ? 'is-loading' : ''}"
      on:click={validate}
      disabled={isValidating}>
      Continue
    </button>
  </div>
</form>

<AccountMatchModal
  bind:showModal={showAccountMatchModal}
  username={recoveredUsername}
  declineLabel="Cancel"
  closeOnDecline={true}
  introText="We've found an inactive account that matches the details you provided. It was archived after more than 3 years of inactivity, in line with our data policy. Check the masked details below — if they look like yours, we'll send a verification code to your email to confirm it's you before reinstating the account."
  on:proceed={() => dispatch("pageChange", "/account/verifyidentity")}
/>